import * as React from 'react';
import { LayoutGrid, LayoutList, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

// =====================================================================
// Empty-page chooser (3B-3.5d v2).
//
// Three equal-weight action cards. Each card opens its OWN modal —
// AI chat, starter-stack picker, or layout picker. The chooser
// itself stays a clean trigger surface; sub-flows live in modals
// for visual consistency with the rest of the editor's picker UX.
// =====================================================================

export type ChooserAction = 'ai' | 'starter-stack' | 'browse-layouts';

export interface EmptyPageChooserProps {
  onPickAction: (action: ChooserAction) => void;
  // While a starter-stack / layout PATCH is in flight, dim and disable
  // every card. The pending card spins a sage loader.
  pendingAction: ChooserAction | null;
}

export function EmptyPageChooser({ onPickAction, pendingAction }: EmptyPageChooserProps) {
  const disabled = pendingAction !== null;

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-12 md:py-16">
      <div className="max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground">
          Let's get this page going.
        </h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Three ways to start. Pick whichever feels right — you can change anything later.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
        <ActionCard
          icon={<Sparkles className="size-6 text-brand-600" />}
          title="Let AI build it for me"
          helper="Describe your business in one sentence. AI fills the whole page in about 15 seconds."
          footerPill="Uses 1 monthly AI generation"
          disabled={disabled}
          pending={pendingAction === 'ai'}
          onClick={() => onPickAction('ai')}
        />
        <ActionCard
          icon={<LayoutList className="size-6 text-text-secondary" />}
          title="Start with a starter stack"
          helper="Pick one of three preset block compositions and edit each block yourself."
          footerPill="No AI used"
          disabled={disabled}
          pending={pendingAction === 'starter-stack'}
          onClick={() => onPickAction('starter-stack')}
        />
        <ActionCard
          icon={<LayoutGrid className="size-6 text-text-secondary" />}
          title="Pick a starting layout"
          helper="Browse 6 ready-made layouts (service business, salon, restaurant, contractor, solo pro, minimal)."
          footerPill="6 layouts to choose from"
          disabled={disabled}
          pending={pendingAction === 'browse-layouts'}
          onClick={() => onPickAction('browse-layouts')}
        />
      </div>

      <p className="mt-10 text-center text-xs text-text-tertiary">
        None of these are permanent. You can switch anything later with the "Start over" link
        in the editor.
      </p>
    </div>
  );
}

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  helper: string;
  footerPill: string;
  disabled: boolean;
  pending: boolean;
  onClick: () => void;
}

function ActionCard({
  icon,
  title,
  helper,
  footerPill,
  disabled,
  pending,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex flex-col items-start gap-3 rounded-xl border border-border-subtle bg-card p-6 text-left transition-[border-color,box-shadow]',
        'hover:border-brand-500 hover:shadow-sm',
        'focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        pending && 'border-brand-500 ring-2 ring-brand-500',
      )}
    >
      <div className="flex h-8 items-center">
        {pending ? (
          <Loader2 className="size-6 animate-spin text-brand-600" aria-label="Working…" />
        ) : (
          icon
        )}
      </div>
      <div className="space-y-1.5">
        <div className="text-lg font-semibold tracking-tight text-foreground">{title}</div>
        <p className="text-sm leading-relaxed text-text-secondary">{helper}</p>
      </div>
      <div className="mt-auto pt-3">
        <span className="text-xs text-text-tertiary">{footerPill}</span>
      </div>
    </button>
  );
}
